/**
 * usePreviewFrames — the wall preview probe (`thinking/plans/2026_09_12_wall_preview_frames.md`,
 * § Carrier "Files"): for each row currently `rendering`, request frame 1 once 2s have passed
 * since it was first seen rendering, then frame k+1 only once frame k has actually loaded (an
 * `<img>`'s own `load` event — the browser's own confirmation the frame exists, not a guess at
 * when the service might have written it). A miss (`error`, most often the "before the frame
 * exists" 404) is retried on the next poll tick, never surfaced as a client-side error — the
 * generation-side plan calls this exact race out (§ Carrier "Verification" item 2: "the first as a
 * 404 or two then a 200"). No storage beyond this hook's own component state: a Back/bfcache
 * restore remounts it with nothing carried over, so every row simply re-probes from frame 1 — the
 * frames are still on S3 for a day (the lifecycle rule, `deployment/environments/prod/eu_central_1/
 * s3.tf`), so this always finds them again.
 *
 * Piggybacks on `useMine`'s own poll cadence rather than running a second timer: the caller
 * (`Wall.tsx`) derives `renderingRowIds` with a `useMemo` keyed on `mine`, so it is a fresh array
 * on every `mine` poll tick — this hook's effect re-evaluates every ~2s while anything is pending,
 * exactly the "next poll tick" cadence the plan names, with no second clock to drift against the
 * first useMine already runs.
 */
import {useEffect, useRef, useState} from 'react'
import {WALL_PREVIEW_FRAMES} from '@core/wall/contract'
import type {PreviewFrame} from './WorkingCell'

const FIRST_FRAME_DELAY_MS = 2_000

type RowProbeState = {
    /** 1-based; the frame ordinal this hook will try next. */
    nextToTry: number
    /** Highest frame ordinal successfully loaded so far, 0 = none yet. */
    loadedN: number
    /** An `Image()` probe for `nextToTry` is currently in flight — guards against firing a second
     *  one for the same (rowId, n) on the next tick before this one has settled. */
    inFlight: boolean
}

export function usePreviewFrames(
    renderingRowIds: readonly string[],
    previewImageUrl: (rowId: string, n: number) => string,
): ReadonlyMap<string, PreviewFrame> {
    const firstSeenRef = useRef<Map<string, number>>(new Map())
    const probeRef = useRef<Map<string, RowProbeState>>(new Map())
    const [frames, setFrames] = useState<ReadonlyMap<string, PreviewFrame>>(new Map())

    useEffect(() => {
        const activeIds = new Set(renderingRowIds)
        const now = Date.now()

        // Garbage-collect rows no longer rendering (done, failed, or reclaimed back to queued) —
        // a later render of the same chain mints a fresh `generations.id` (rowId), so a dropped
        // key is never reused by a different take.
        for (const rowId of firstSeenRef.current.keys()) {
            if (!activeIds.has(rowId)) {
                firstSeenRef.current.delete(rowId)
                probeRef.current.delete(rowId)
            }
        }
        setFrames((prev) => {
            let changed = false
            const next = new Map(prev)
            for (const rowId of next.keys()) {
                if (!activeIds.has(rowId)) {
                    next.delete(rowId)
                    changed = true
                }
            }
            return changed ? next : prev
        })

        for (const rowId of activeIds) {
            if (!firstSeenRef.current.has(rowId)) firstSeenRef.current.set(rowId, now)
            if (!probeRef.current.has(rowId)) {
                probeRef.current.set(rowId, {nextToTry: 1, loadedN: 0, inFlight: false})
            }

            const t0 = firstSeenRef.current.get(rowId) as number
            const state = probeRef.current.get(rowId) as RowProbeState

            if (state.inFlight) continue
            if (state.loadedN >= WALL_PREVIEW_FRAMES) continue
            if (state.nextToTry === 1 && now - t0 < FIRST_FRAME_DELAY_MS) continue

            const n = state.nextToTry
            state.inFlight = true
            const probe = new Image()
            probe.onload = () => {
                state.inFlight = false
                state.loadedN = n
                state.nextToTry = n + 1
                setFrames((prev) => {
                    const next = new Map(prev)
                    next.set(rowId, {url: previewImageUrl(rowId, n), n: n as 1 | 2 | 3})
                    return next
                })
            }
            probe.onerror = () => {
                // Premature probe — retried on the next poll tick, same n, no client-visible error.
                state.inFlight = false
            }
            probe.src = previewImageUrl(rowId, n)
        }
    }, [renderingRowIds, previewImageUrl])

    return frames
}
